import {
    Flex,
    Text,
    IconButton,
    Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    Box,
} from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { AuthenticatedComponent } from '../../auth/keycloak';
import PageDataContext from '../../context/PageDataContext';
import { usePageDataCache } from '../../hooks/usePageDataCache';
import {
    getPaymentIntent,
    getProductByIDs,
    ProductDef,
    WrapperLicense,
} from '../../service/inventory';
import { StripePaymentIntent } from '../../service/stripe';
import { InformationIcon } from '../iconComponents/InformationIcon';
import { PendingPaymentItem } from './PendingPaymentItem';

const LogPrefix = 'PendingPayment';

const productKey = 'streamsix_product_id';

interface PendingPaymentProps {}

export const PendingPayments: AuthenticatedComponent<PendingPaymentProps> = ({ keycloak }) => {
    const { isLoadFromCache } = useContext(PageDataContext);
    const [allItems, setItems] = usePageDataCache<{ [date: string]: StripePaymentIntent[] }>(
        'stripe_payment_intents',
        {},
    );
    const [sortedDates, setSortedDates] = usePageDataCache<string[]>('sortedDates', []);
    const [products, setProducts] = usePageDataCache<ProductDef[]>('stripe_payment_products', []);

    const [displayPending, setDisplayPending] = useState<boolean>(false);

    const fetchData = async () => {
        const logPrefix = `[${LogPrefix}.fetchData]`;

        console.log(`${logPrefix} starting`);

        try {
            let nextPage = '';
            const items: StripePaymentIntent[] = [];
            do {
                // query payment intents
                const result = await getPaymentIntent(nextPage, keycloak.token);
                items.push(...result.data);
                nextPage = result.page.next;
                if (!nextPage) {
                    break;
                }
            } while (true);

            console.log(`${logPrefix} receive: ${JSON.stringify(items)}`);

            const datedItems: { [date: string]: StripePaymentIntent[] } = {};
            items.forEach((x) => {
                const thisDate = new Date(x.created * 1000);
                const dateString = thisDate.toISOString().split('T')[0];
                let list = datedItems[dateString];
                if (!list) {
                    list = [];
                    datedItems[dateString] = list;
                }
                if (!x.id || !list.find((y) => y.id === x.id)) {
                    list.push(x);
                }
            });
            setItems(datedItems);
            getProducts(items);
        } catch (ex) {
            console.error(`${logPrefix} ${ex}`);
        }

        console.log(`${logPrefix} done`);
    };

    const getProducts = async (datas: StripePaymentIntent[]) => {
        const filtered = datas.filter(
            (x) => x.metadata && productKey in x.metadata && x.metadata[productKey] !== '',
        );
        const ids = filtered.map((data) => {
            return data.metadata[productKey];
        });
        const mySet = new Set(ids);
        const p = await getProductByIDs(Array.from(mySet), WrapperLicense, keycloak.token);
        setProducts(p);
    };

    useEffect(() => {
        // order by date descending
        setSortedDates(
            Array.from(Object.keys(allItems)).sort((n1, n2) => {
                return n1 > n2 ? -1 : 1;
            }),
        );
    }, [allItems]);

    useEffect(() => {
        if (!isLoadFromCache) {
            fetchData();
        }
    }, []);

    return (
        <Flex className={'transaction-pending'}>
            <Flex className={'transaction-pending-info'}>
                <Text>Pending Payments:</Text>
                <Popover>
                    <PopoverTrigger>
                        <IconButton
                            variant={'unstyled'}
                            icon={<InformationIcon />}
                            aria-label={'payments-info'}
                        />
                    </PopoverTrigger>
                    <PopoverContent>
                        <PopoverHeader>Pending payments:</PopoverHeader>
                        <PopoverBody>Pending Payments are held until they are cleared.</PopoverBody>
                    </PopoverContent>
                </Popover>
                <Flex className={'transaction-spacer'} />
                <Button
                    className={'view-button'}
                    onClick={() => {
                        setDisplayPending(!displayPending);
                    }}
                >
                    {displayPending ? 'COLLAPSE' : 'VIEW'}
                </Button>
            </Flex>

            {displayPending && (
                <Flex>
                    <Box className={'wrapper'}>
                        {sortedDates.length === 0 ? (
                            <Text>No Pending Payments</Text>
                        ) : (
                            sortedDates.map((date, idx) => {
                                const items = allItems[date];
                                return (
                                    <PendingPaymentItem
                                        datas={items && [...items]}
                                        index={idx}
                                        date={date}
                                        products={products}
                                        keycloak={keycloak}
                                        key={idx}
                                    />
                                );
                            })
                        )}
                    </Box>
                </Flex>
            )}
        </Flex>
    );
};
